
import { Component, Vue, Prop, Watch } from "vue-property-decorator";
import httpClient from "../../store/httpClient";
import { InvoiceEditDto } from "@/dtos/invoiceEditDto";
import rules from "@/store/rules";
import { InvoicePositionDto } from "@/dtos/invoicePositionDto";
import EditInvoicePosition from "./EditInvoicePosition.vue";
import DatePicker from "@/components/DatePicker.vue";

@Component({
	components: {
		EditInvoicePosition,
		"date-picker": DatePicker,
	},
})
export default class EditInvoice extends Vue {
	@Prop() private invoiceId?: string;
	private invoice: InvoiceEditDto = new InvoiceEditDto();
	private isLoading: boolean = false;
	private isSaving: boolean = false;
	private isDownloading: boolean = false;
	private validForm: boolean = false;
	private requiredRule: any = rules.requiredRule;
	private requiredStringMax50Rule: any = rules.requiredStringMax50Rule;
	private newPosition: any = "";
	private newPositionName: string = "";
	private newPositionNameDialog: boolean = false;
	private gefilterteArtikel: any = [];
	private articleFilter: string = "";

	private async mounted() {
		await this.loadInvoice();
	}

	private get invoiceIdNumber() {
		return this.invoiceId ? parseInt(this.invoiceId) : 0;
	}

	private get partPrice(): number {
		return this.invoice.positions.reduce(
			(sum: number, a: InvoicePositionDto) => sum + a.pricePerUnit * a.quantity,
			0
		);
	}

	private get mwstAmount(): number {
		return (this.partPrice / 100) * this.invoice.mwstSatz;
	}

	private get totalAmount() {
		return this.partPrice + this.mwstAmount;
	}

	private get canBeSaved(): boolean {
		return (
			this.validForm &&
			this.invoice.positions.length > 0 &&
			this.invoice.positions.every((p: InvoicePositionDto) => p.valid)
		);
	}

	@Watch("articleFilter")
	private async loadArticle() {
		try {
			const generalArticleResponse = await httpClient().get(
				"Article/GetGeneralArticles?" + `searchTerm=${this.articleFilter}`
			);
			for (const iterator of generalArticleResponse.data) {
				iterator.text = iterator.id + " | " + iterator.name;
			}
			this.gefilterteArtikel = generalArticleResponse.data;
		} catch {
			console.error("Load article failed.");
		}
	}

	private setArticle() {
		if (this.newPosition) {
			// Ein Objekt in der Liste wurde ausgewählt
			// Wir speichern aber immer nur den Namen
			this.newPositionName = this.newPosition.name;
		}
	}

	private addPosition() {
		console.log("adding position");
		// Neue absteigende Id wird erstellt
		// Die Id wird auch als Id für das DOM element benutzt, deshalb muss sie einzigartig sein.
		// Verwendung von "index" im v-for funktioniert nicht, da es bei mehrfachem hinzufügen / entfernen dazu führen kann,
		// Dass Daten zwischen Elementen verschoben werden.
		// Die Id ist negativ gewählt, sodass das Backend sicher keine dazugehörige DB-Position findet und regulär eine korrekte Id vergibt bei neuen Positionen.
		let newId = this.invoice.positions.every((p: InvoicePositionDto) => p.id > 0)
			? -1
			: Math.min(...this.invoice.positions.map((p: InvoicePositionDto) => p.id)) - 1;
		let newPosition: InvoicePositionDto = {
			articleId: 0,
			articleName: this.newPositionName,
			customerArticleName: "",
			id: newId,
			materialname: "",
			note: "",
			position:
				Math.max(...this.invoice.positions.map((p: InvoicePositionDto) => p.position)) + 10,
			pricePerUnit: 0.0,
			quantity: 1,
			valid: true,
		};
		this.invoice.positions.push(newPosition);
		this.newPosition = "";
		this.newPositionName = "";
	}

	private deletePosition(positionId: number) {
		let removeIndex = this.invoice.positions.findIndex(
			(position: InvoicePositionDto) => position.id == positionId
		);
		this.invoice.positions.splice(removeIndex, 1);
	}

	private async loadInvoice() {
		if (this.isLoading || this.isSaving) {
			return;
		}
		try {
			this.isLoading = true;
			console.log("Loading invoice for editing...");
			const response = await httpClient().get(
				`Invoice/GetInvoiceForEdit?invoiceId=${this.invoiceId}`
			);
			response.data.positions.forEach((p: InvoicePositionDto) => (p.valid = true)); // init as true
			this.invoice = response.data;
		} catch (e) {
			this.$store.commit("setSnackbarErrorText", "Rechnung konnte nicht geladen werden.");
			console.log("Loading invoice failed.", e);
		} finally {
			this.isLoading = false;
		}
	}

	private async saveInvoice() {
		if (this.isLoading || this.isSaving || !this.canBeSaved) {
			return;
		}
		try {
			this.isSaving = true;
			console.log("Saving changes to invoice...");
			await httpClient().post(`Invoice/EditInvoice`, this.invoice);
			this.$store.commit("setSnackbarText", "Änderungen erfolgreich gespeichert.");
		} catch (e) {
			console.log("Loading invoice failed.", e);
			this.$store.commit("setSnackbarErrorText", "Fehler beim Speichern der Änderungen.");
		} finally {
			this.isSaving = false;
		}
	}

	private async downloadPDF() {
		if (this.isDownloading) {
			return;
		}
		try {
			this.isDownloading = true;
			await this.$store.dispatch("downloadInvoiceAsPdf", this.invoice.invoiceId);
		} catch {
			console.log("Failed to download invoice pdf.");
		} finally {
			this.isDownloading = false;
		}
	}

	private abort() {
		this.$router.go(-1);
	}

	private getStateText(enumValue: number) {
		switch (enumValue) {
			case 10: {
				return "Unvollständig";
			}
			case 20: {
				return "Gültig";
			}
			case 30: {
				return "Bezahlt";
			}
			case 40: {
				return "Storniert";
			}
			default: {
				return "N/A";
			}
		}
	}
}
